type Child = Node | string | number | null | undefined | false;
type Props = {
  class?: string;
  style?: Partial<CSSStyleDeclaration> | string;
  dataset?: Record<string, string>;
  on?: Partial<{ [K in keyof HTMLElementEventMap]: (ev: HTMLElementEventMap[K]) => void }>;
  [attr: string]: unknown;
};

/** Tiny hyperscript helper for building DOM UI without a framework. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Props | null = null,
  ...children: (Child | Child[])[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value === undefined || value === null || value === false) continue;
      if (key === 'class') el.className = String(value);
      else if (key === 'style') {
        if (typeof value === 'string') el.setAttribute('style', value);
        else
          for (const [prop, v] of Object.entries(value as Record<string, string>)) {
            // Custom properties (--foo) only work through setProperty.
            if (prop.startsWith('--')) el.style.setProperty(prop, v);
            else (el.style as unknown as Record<string, string>)[prop] = v;
          }
      } else if (key === 'dataset') Object.assign(el.dataset, value);
      else if (key === 'on') {
        for (const [ev, fn] of Object.entries(value as Record<string, EventListener>)) el.addEventListener(ev, fn);
      } else if (key in el && typeof value !== 'string') {
        (el as unknown as Record<string, unknown>)[key] = value;
      } else el.setAttribute(key, value === true ? '' : String(value));
    }
  }
  append(el, children);
  return el;
}

function append(el: Node, children: (Child | Child[])[]): void {
  for (const c of children) {
    if (Array.isArray(c)) append(el, c);
    else if (c !== null && c !== undefined && c !== false) el.appendChild(typeof c === 'object' ? c : document.createTextNode(String(c)));
  }
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Replace children only (keeps the element and its listeners). */
export function setChildren(el: Element, ...children: (Child | Child[])[]): void {
  clear(el);
  append(el, children);
}

export function $<T extends Element = HTMLElement>(root: ParentNode, sel: string): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error(`missing element ${sel}`);
  return el;
}

/** Wait for a CSS animation/transition to finish, with a timeout fallback. */
export function waitAnim(el: Element, fallbackMs: number): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (done) return;
      done = true;
      el.removeEventListener('animationend', onEnd);
      el.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
      resolve();
    };
    const onEnd = (e: Event): void => {
      if (e.target === el) finish();
    };
    el.addEventListener('animationend', onEnd);
    el.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(finish, fallbackMs);
  });
}
